// Simple in-memory rate limiter for client-side protection
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  
  /**
   * Check if request is allowed based on rate limit
   * @param key - Unique identifier (e.g., user ID, IP, endpoint)
   * @param limit - Maximum number of requests
   * @param windowMs - Time window in milliseconds
   * @returns true if allowed, false if rate limit exceeded
   */
  isAllowed(key: string, limit: number = 10, windowMs: number = 60000): boolean {
    const now = Date.now();
    const timestamps = this.requests.get(key) || [];
    
    // Remove timestamps outside the window
    const validTimestamps = timestamps.filter(t => now - t < windowMs);
    
    // Check if limit exceeded
    if (validTimestamps.length >= limit) {
      return false;
    }
    
    // Add current timestamp
    validTimestamps.push(now);
    this.requests.set(key, validTimestamps);
    
    return true;
  }
  
  /**
   * Reset rate limit for a specific key
   */
  reset(key: string): void {
    this.requests.delete(key);
  }
  
  /**
   * Clear all rate limits
   */
  clearAll(): void {
    this.requests.clear();
  }
}

export const rateLimiter = new RateLimiter();

/**
 * Rate limit configuration for different endpoints
 */
export const RATE_LIMITS = {
  // Chat messages: 10 per minute
  CHAT_MESSAGE: { limit: 10, windowMs: 60000 },
  
  // Tipping: 5 per minute
  TIP: { limit: 5, windowMs: 60000 },
  
  // Poll voting: 3 per minute
  POLL_VOTE: { limit: 3, windowMs: 60000 },
  
  // Wallet top-up: 3 per 5 minutes
  WALLET_TOPUP: { limit: 3, windowMs: 300000 },
  
  // Stream creation: 2 per hour
  STREAM_CREATE: { limit: 2, windowMs: 3600000 },
  
  // API calls: 100 per minute
  API_CALL: { limit: 100, windowMs: 60000 },
} as const;
